(() => {
  function initialise(scope) {
    const root = scope.querySelector('[data-preface]');
    const trigger = scope.querySelector('[data-preface-trigger]');
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    root.classList.add('is-enhanced');
    const label = trigger?.querySelector('[data-preface-trigger-label]');
    const choices = [...root.querySelectorAll('[data-preface-choice]')];
    const siteChoices = [...scope.querySelectorAll('[data-litq-site-language]')];
    const languageMenu = scope.querySelector('[data-litq-language-menu]');
    label?.setAttribute('data-i18n-dynamic', '');
    const status = root.querySelector('[data-preface-status]');
    const content = root.querySelector('[data-preface-content]');
    const popout = root.querySelector('[data-preface-popout]');
    const dialog = root.querySelector('[data-preface-dialog]');
    const dialogBody = dialog?.querySelector('[data-preface-dialog-body]');
    const dialogTitle = dialog?.querySelector('[data-preface-dialog-title]');
    const frameTitle = root.querySelector('[data-preface-frame-title]');
    const dialogClose = dialog?.querySelector('[data-preface-dialog-close]');
    const supportsDialog = typeof dialog?.showModal === 'function' && typeof dialog?.close === 'function';
    const contentHome = content?.parentNode;
    const contentNext = content?.nextSibling;
    let returningFromDialog = false;
    let movingContent = false;
    let frameMinimum = null;
    const languages = [...root.querySelectorAll('[data-preface-language]')];
    const tracks = languages.map((article) => ({
      article,
      audio: article.querySelector('audio'),
      transcript: article.querySelector('.ac-litq__preface-transcript'),
      words: [...article.querySelectorAll('[data-word]')],
      cues: null,
      loading: null,
      controller: null,
      cuesError: false,
      pendingPlays: new Set(),
      active: -1,
    }));
    const initialLanguage = 'en';
    let current = tracks.find((track) => track.article.dataset.prefaceLanguage === initialLanguage) || tracks[0];
    let frame;
    let generation = 0;
    let playbackRequested = false;
    const messages = {
      listen: 'Listen to the project overview',
      pause: 'Pause project overview',
      continue: 'Continue project overview',
      read: 'Read the project overview',
      loading: 'Loading AI-generated narration…',
      narration: 'AI-generated narration · Follow along with the transcript.',
      timingUnavailable: 'Audio is available, but word highlighting could not load. Please try again.',
      playbackUnavailable: 'Playback could not start. Use the audio play control to try again; the full text is available below.',
      finished: 'Project overview finished. You can listen again or read the transcript.',
      audioUnavailable: 'The audio could not load. Please try again; you can still read the project overview below.',
      transcriptOnly: 'The translated project overview is available below. Narration is not available in this language yet.',
      popout: 'Open larger reader',
      closeReader: 'Close larger reader',
    };
    const translate = (key, fallback = messages[key], vars = {}) => {
      const text = window.LITQI18n?.t(`ui.preface.${key}`, fallback, vars) ?? fallback;
      return text.replace(/\{(\w+)\}/g, (match, name) => vars[name] ?? match);
    };
    let statusKey = current.audio ? 'narration' : 'transcriptOnly';
    const setStatus = (key) => { statusKey = key; status.textContent = key === 'narration' ? '' : translate(key); status.hidden = key === 'narration'; };
    const updateButton = () => {
      if (!trigger || !label) return;
      trigger.removeAttribute('aria-expanded');
      trigger.setAttribute('aria-pressed', String(playbackRequested));
      label.textContent = translate(!current.audio ? 'read' : playbackRequested ? 'pause'
        : current.audio.currentTime > 0 && !current.audio.ended ? 'continue' : 'listen');
    };
    const clear = (track) => {
      if (track.active >= 0) {
        track.words[track.active]?.classList.remove('is-speaking');
        track.words[track.active]?.removeAttribute('aria-current');
      }
      track.active = -1;
    };
    const keepWordInFrame = (track) => {
      if (track.active < 0) return;
      const word = track.words[track.active];
      if (!word) return;
      const bounds = track.transcript.getBoundingClientRect();
      const rect = word.getBoundingClientRect();
      if (rect.top < bounds.top + 18 || rect.bottom > bounds.bottom - 18) {
        // Move only the transcript, so narration never scrolls the page.
        track.transcript.scrollTop += rect.top - bounds.top - bounds.height * 0.35;
      }
    };
    const highlight = () => {
      const track = current;
      if (!track.audio || !track.cues) return;
      const time = track.audio.currentTime;
      let low = 0;
      let high = track.cues.length - 1;
      let found = -1;
      while (low <= high) {
        const mid = (low + high) >> 1;
        if (track.cues[mid].start <= time) { found = mid; low = mid + 1; }
        else high = mid - 1;
      }
      const cue = track.cues[found];
      const index = cue && time < cue.end ? cue.index : -1;
      if (index === track.active) { keepWordInFrame(track); return; }
      clear(track);
      if (index < 0) return;
      const word = track.words[index];
      if (!word) return;
      word.classList.add('is-speaking');
      word.setAttribute('aria-current', 'true');
      track.active = index;
      keepWordInFrame(track);
    };
    const tick = () => {
      highlight();
      if (playbackRequested && current.audio && !current.audio.paused) frame = requestAnimationFrame(tick);
    };
    const cancelPending = () => {
      generation += 1;
      cancelAnimationFrame(frame);
      tracks.forEach((track) => {
        track.controller?.abort();
        track.controller = null;
        track.loading = null;
      });
    };
    const loadCues = (track) => {
      if (!track.audio) return Promise.resolve(null);
      if (track.cues) return Promise.resolve(track.cues);
      if (!track.loading) {
        const controller = new AbortController();
        track.controller = controller;
        track.cuesError = false;
        track.loading = fetch(track.article.dataset.cues, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Transcript timing unavailable');
            return response.json();
          })
          .then((data) => {
            if (controller.signal.aborted) return null;
            if (!Array.isArray(data.words) || !data.words.every((cue) => Number.isFinite(cue.start) && Number.isFinite(cue.end) && Number.isInteger(cue.index) && cue.index >= 0 && cue.index < track.words.length)) throw new Error('Invalid transcript timing');
            track.cues = data.words;
            return track.cues;
          })
          .catch(() => {
            if (!controller.signal.aborted) track.cuesError = true;
            return null;
          })
          .finally(() => {
            if (track.controller === controller) {
              track.controller = null;
              track.loading = null;
            }
          });
      }
      return track.loading;
    };
    const loadTiming = (track, request) => loadCues(track).then((cues) => {
      if (request !== generation || track !== current) return null;
      if (!cues) {
        if (track.cuesError) setStatus('timingUnavailable');
        return null;
      }
      if (!track.audio.paused) highlight();
      return cues;
    });
    const play = async () => {
      const track = current;
      const request = ++generation;
      if (track.audio?.ended) {
        track.audio.currentTime = 0;
        track.transcript.scrollTop = 0;
        clear(track);
      }
      playbackRequested = Boolean(track.audio);
      updateButton();
      if (!track.audio) { setStatus('transcriptOnly'); return; }
      setStatus('loading');
      loadTiming(track, request);
      track.pendingPlays.add(request);
      try {
        await track.audio.play();
        if (request !== generation || current !== track) {
          // An older play promise must not pause a newer visit to the same track.
          if (current !== track || !playbackRequested) {
            track.audio.pause();
            if (current !== track) track.audio.currentTime = 0;
          }
          return;
        }
        setStatus(track.cuesError ? 'timingUnavailable' : 'narration');
      } catch {
        if (request === generation && current === track) {
          playbackRequested = false;
          setStatus('playbackUnavailable');
        }
      } finally {
        track.pendingPlays.delete(request);
      }
      updateButton();
    };
    // Move one reader, including its existing media element, into the dialog.
    // moveBefore preserves browser media/focus state; the fallback resumes the
    // same element at its previous position if a browser pauses on reparenting.
    const moveContent = (parent, before = null) => {
      if (!content || !parent || (content.parentNode === parent && content.nextSibling === before)) return;
      const track = current;
      const time = track.audio?.currentTime;
      const wasPlaying = Boolean(track.audio && !track.audio.paused);
      const request = generation;
      let preserved = false;
      movingContent = true;
      try {
        if (typeof parent.moveBefore === 'function') {
          try { parent.moveBefore(content, before); preserved = true; }
          catch { parent.insertBefore(content, before); }
        } else parent.insertBefore(content, before);
      } finally { movingContent = false; }
      if (!preserved) {
        if (track.audio && Number.isFinite(time) && track.audio.currentTime !== time) track.audio.currentTime = time;
        if (wasPlaying && track.audio.paused && track === current && request === generation) {
          playbackRequested = true;
          track.audio.play().catch(() => {
            if (track === current && request === generation) {
              playbackRequested = false;
              setStatus('playbackUnavailable');
              updateButton();
            }
          });
        }
      }
      keepWordInFrame(current);
    };
    const restoreReader = () => {
      if (returningFromDialog) return;
      returningFromDialog = true;
      const moved = content && content.parentNode !== contentHome;
      if (moved) moveContent(contentHome, contentNext?.parentNode === contentHome ? contentNext : dialog);
      if (frameMinimum) {
        if (frameMinimum.value) root.style.setProperty('min-block-size', frameMinimum.value, frameMinimum.priority);
        else root.style.removeProperty('min-block-size');
        frameMinimum = null;
      }
      popout?.setAttribute('aria-expanded', 'false');
      if (moved) popout?.focus({ preventScroll: true });
      returningFromDialog = false;
    };
    const closeReader = () => {
      if (!dialog?.open) return;
      restoreReader();
      dialog.close();
      // Native dialog.close may restore the previously focused word. Keep the
      // explicit return target outside the closed dialog and avoid page jumps.
      popout?.focus({ preventScroll: true });
    };
    const openReader = () => {
      if (!supportsDialog || !content || !dialogBody || dialog.open) return;
      try { dialog.showModal(); } catch { return; }
      frameMinimum = { value: root.style.getPropertyValue('min-block-size'), priority: root.style.getPropertyPriority('min-block-size') };
      const style = window.getComputedStyle(root);
      const extras = style.boxSizing === 'border-box' ? 0 : ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth'].reduce((sum, key) => sum + (parseFloat(style[key]) || 0), 0);
      root.style.setProperty('min-block-size', `${Math.max(0, root.getBoundingClientRect().height - extras)}px`);
      try { moveContent(dialogBody); }
      catch { closeReader(); return; }
      popout.setAttribute('aria-expanded', 'true');
      dialogClose?.focus({ preventScroll: true });
    };
    trigger?.addEventListener('click', () => {
      if (!playbackRequested) {
        if (!dialog?.open) root.scrollIntoView?.({ block: 'start', behavior: 'instant' });
        play();
      } else {
        cancelPending();
        playbackRequested = false;
        current.audio?.pause();
        updateButton();
      }
    });
    popout?.addEventListener('click', openReader);
    dialogClose?.addEventListener('click', closeReader);
    dialog?.addEventListener('cancel', (event) => { event.preventDefault(); closeReader(); });
    dialog?.addEventListener('close', restoreReader);
    dialog?.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeReader();
    });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll('button, input, audio[controls], [tabindex]')]
        .filter((item) => !item.disabled && item.tabIndex >= 0 && !item.closest('[hidden]'));
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    const actions = root.querySelector('[data-preface-actions]');
    const copyButton = root.querySelector('[data-preface-copy]');
    const shareButton = root.querySelector('[data-preface-share]');
    const actionStatus = root.querySelector('[data-preface-action-status]');
    const copyFallback = root.querySelector('[data-preface-copy-fallback]');
    const actionLabels = () => current.article.dataset.prefaceLanguage === 'pt'
      ? ['Copiar texto', 'Compartilhar texto', 'Texto copiado.', 'Selecione e copie o texto abaixo.', 'Texto da visão geral']
      : ['Copy text', 'Share text', 'Text copied.', 'Select and copy the text below.', 'Overview text'];
    const overviewText = () => current.transcript.textContent.trim();
    const copyText = async () => {
      const text = overviewText();
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(text);
        actionStatus.textContent = actionLabels()[2];
      } catch {
        copyFallback.value = text;
        copyFallback.hidden = false;
        copyFallback.focus();
        copyFallback.select();
        actionStatus.textContent = actionLabels()[3];
      }
    };
    copyButton?.addEventListener('click', copyText);
    shareButton?.addEventListener('click', async () => {
      if (!navigator.share) { await copyText(); return; }
      try {
        await navigator.share({ title: current.article.querySelector('h3')?.textContent || 'LITQ', text: overviewText() });
      } catch (error) {
        if (error.name !== 'AbortError') await copyText();
      }
    });
    if (actions) actions.hidden = false;
    const reset = (track) => {
      track.audio?.pause();
      if (track.audio) track.audio.currentTime = 0;
      clear(track);
      track.transcript.scrollTop = 0;
    };
    const syncChoices = () => {
      const language = current.article.dataset.prefaceLanguage;
      [...choices, ...siteChoices].forEach((item) => { item.checked = item.value === language; });
      const selectedLabel = siteChoices.find((item) => item.value === language)?.closest('label')?.querySelector('bdi')?.textContent || language.toUpperCase();
      scope.querySelectorAll('[data-litq-current-language]').forEach((item) => { item.textContent = selectedLabel; });
      tracks.forEach((track) => { track.article.hidden = track !== current; });
    };
    const renderUi = () => {
      if (copyButton) copyButton.textContent = actionLabels()[0];
      if (shareButton) shareButton.textContent = actionLabels()[1];
      if (actionStatus) actionStatus.textContent = '';
      if (copyFallback) { copyFallback.hidden = true; copyFallback.value = ''; copyFallback.setAttribute('aria-label', actionLabels()[4]); }
      updateButton();
      setStatus(statusKey);
      const title = current.article.querySelector('h3')?.textContent || 'Project overview';
      if (frameTitle) frameTitle.textContent = title;
      if (dialogTitle) dialogTitle.textContent = title;
      if (popout) {
        const text = popout.querySelector('[data-preface-popout-label]') || popout;
        text.textContent = translate('popout');
      }
      if (dialogClose) {
        const text = dialogClose.querySelector('[data-preface-dialog-close-label]') || dialogClose.querySelector('[data-i18n]');
        if (text) text.textContent = translate('closeReader');
        else dialogClose.setAttribute('aria-label', translate('closeReader'));
      }
      current.words.forEach((word) => {
        if (current.audio) word.setAttribute('aria-label', translate('playFrom', 'Play from: {word}', { word: word.textContent }));
      });
    };
    const changeLanguage = (language, source) => {
      const selected = tracks.find((track) => track.article.dataset.prefaceLanguage === language);
      if (!selected) return;
      if (selected === current) { syncChoices(); queueMicrotask(renderUi); return; }
      const shouldPlay = source === 'overview' || (source !== 'restore' && playbackRequested);
      cancelPending();
      playbackRequested = false;
      reset(current);
      reset(selected);
      current = selected;
      syncChoices();
      setStatus(current.audio ? 'narration' : 'transcriptOnly');
      if (shouldPlay) play();
      queueMicrotask(renderUi);
    };
    const emitLanguage = (language, source) => document.dispatchEvent(new CustomEvent('litq:language-change', { detail: { language, source } }));
    choices.forEach((choice) => choice.addEventListener('change', () => {
      if (choice.checked) emitLanguage(choice.value, 'overview');
    }));
    siteChoices.forEach((choice) => choice.addEventListener('change', () => {
      if (choice.checked) {
        emitLanguage(choice.value, 'hero');
        if (languageMenu?.open) {
          languageMenu.open = false;
          languageMenu.querySelector('summary').focus({ preventScroll: true });
        }
      }
    }));
    if (languageMenu) {
      languageMenu.hidden = false;
      document.addEventListener('pointerdown', (event) => {
        if (!languageMenu.contains(event.target)) languageMenu.open = false;
      });
      languageMenu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        languageMenu.open = false;
        languageMenu.querySelector('summary').focus({ preventScroll: true });
      });
    }
    document.addEventListener('litq:language-change', (event) => {
      if (!root.isConnected) return;
      changeLanguage(event.detail?.language, event.detail?.source);
    });
    document.addEventListener('litq:translation-applied', () => {
      if (root.isConnected) renderUi();
    });
    tracks.forEach((track) => {
      track.article.hidden = track !== current;
      const seekToWord = async (word) => {
        if (!track.audio || track !== current) return;
        const request = ++generation;
        playbackRequested = true;
        const cues = await loadTiming(track, request);
        if (request !== generation || track !== current) return;
        const cue = cues?.find((item) => item.index === Number(word.dataset.word));
        if (!cue) { playbackRequested = !track.audio.paused; updateButton(); return; }
        track.audio.currentTime = cue.start;
        highlight();
        play();
      };
      track.words.forEach((word) => {
        if (!track.audio) return;
        word.setAttribute('role', 'button');
        word.setAttribute('tabindex', '0');
        word.setAttribute('aria-label', translate('playFrom', 'Play from: {word}', { word: word.textContent }));
      });
      track.transcript.addEventListener('click', (event) => {
        const word = event.target.closest('[data-word]');
        if (word && track.transcript.contains(word)) seekToWord(word);
      });
      track.transcript.addEventListener('keydown', (event) => {
        const word = event.target.closest('[data-word]');
        if (word && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          seekToWord(word);
        }
      });
      if (!track.audio) return;
      track.audio.addEventListener('play', () => {
        if (track.audio.paused) return;
        if (track !== current || (track.pendingPlays.size && !playbackRequested)) {
          track.audio.pause();
          if (track !== current) track.audio.currentTime = 0;
          return;
        }
        if (!playbackRequested) generation += 1;
        playbackRequested = true;
        loadTiming(track, generation);
        cancelAnimationFrame(frame);
        tick();
        updateButton();
      });
      track.audio.addEventListener('pause', () => {
        if (!movingContent && track === current && track.audio.paused) {
          generation += 1;
          playbackRequested = false;
          cancelAnimationFrame(frame);
          updateButton();
        }
      });
      track.audio.addEventListener('timeupdate', () => { if (track === current) highlight(); });
      track.audio.addEventListener('seeked', () => { if (track === current) highlight(); });
      track.audio.addEventListener('ended', () => {
        if (track !== current) return;
        cancelPending();
        playbackRequested = false;
        track.audio.pause();
        clear(track);
        setStatus('finished');
        updateButton();
      });
      track.audio.addEventListener('error', () => {
        if (track === current) setStatus('audioUnavailable');
      });
    });
    scope.querySelector('[data-preface-fallback]')?.setAttribute('hidden', '');
    root.querySelector('[data-preface-toolbar]').hidden = false;
    scope.querySelectorAll('[data-litq-language-selector]').forEach((selector) => { selector.hidden = false; });
    status.setAttribute('data-i18n-dynamic', '');
    syncChoices();
    if (trigger) trigger.hidden = false;
    if (popout) popout.hidden = !supportsDialog;
    renderUi();
  }
  initialise(document);
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
